import { SetStateAction } from "react";
import type { IdWithName } from "@repo/shared/common";
import { CreditNoteKind, DocumentRole, DocumentStatus, DocumentType } from "@repo/server-api";

// Define or import IdWithNameWithEmail if not already available
export type IdWithNameWithEmail = IdWithName & { email: string };

export type IdWithNameWithNumber = IdWithName & { number: string };

export type InvoiceItem = {
	id: string | null;
	name: string;
	description: string | null;
	quantity: number;
	unit: string;
	unitPrice: number;
	discount: number;
	totalPrice: number;
	taxCode: string;
	tax: number;
};

export type CommercialDocumentSummary = {
	id: string;
	number: string;
	leftTotal: number | null;
	grossTotal: number;
	issueDate: string | null;
	dueDate: string | null;
	client: IdWithName;
	contractor: IdWithName;
	contactPerson: IdWithNameWithEmail | null;
	project: IdWithNameWithNumber | null;
	type: DocumentType;
	status: DocumentStatus;
	sentDate?: string | null;
	isInvalid?: boolean;
	isProforma?: boolean;
	cancellationReason?: string | null;
	stornId?: string | null;
	rejectionReason?: string | null;
	cancellationReversalDocumentSentDate?: string | null;
	createdDate?: string | null;
};

export type TaxDetail = {
	code: string;
	netAmount: number;
	rate: number;
	taxAmount: number;
};

export type Invoice = {
	id: string | null;
	client: IdWithName | null;
	contactPerson: IdWithNameWithEmail | null;
	contractor: IdWithName | null;
	project: IdWithName | null;
	paymentDeadlineDays: number;
	headerNote: string | null;
	footerNote: string | null;
	items: InvoiceItem[];

	number: string | null;
	leftTotal: number | null;
	grossTotal: number | null;
	netTotal: number | null;
	notes: string | null;
	issueDate: string | null;
	dueDate: string | null;
	type: DocumentType | null;
	status?: DocumentStatus;

	isProforma?: boolean;
};

export type CreditNote = {
	id: string | null;
	client: IdWithName | null;
	contactPerson: IdWithName | null;
	contractor: IdWithName | null;
	project: IdWithName | null;
	paymentDeadlineDays: number;
	headerNote: string | null;
	footerNote: string | null;
	items: InvoiceItem[];

	number: string | null;
	leftTotal: number | null;
	grossTotal: number | null;
	netTotal: number | null;
	notes: string | null;
	issueDate: string | null;
	dueDate: string | null;
	type: DocumentType | null;
	status?: DocumentStatus;

	isProforma?: boolean;
	isInvalid?: boolean;
	sentDate?: string | null;

	// CreditNote specific fields
	kind: CreditNoteKind;
	documentRole: DocumentRole;
	settlesInvoiceId?: string | null;
};

export type Offer = {
	id: string | null;
	client: IdWithName | null;
	contactPerson: IdWithName | null;
	contractor: IdWithName | null;
	project: IdWithName | null;
	headerNote: string | null;
	footerNote: string | null;
	items: InvoiceItem[];
	number: string | null;
	issueDate: string | null;
	type: DocumentType | null;
	status?: DocumentStatus;

	paymentDeadlineDays: number | null;
	dueDate: string | null;
};

export const createEmptyOffer = (): Offer => {
	return {
		id: "",
		client: null,
		contactPerson: null,
		contractor: null,
		project: null,
		headerNote: null,
		footerNote: null,
		items: [],
		number: "",
		issueDate: null,
		type: DocumentType.Offer,
		status: DocumentStatus.Draft,
		paymentDeadlineDays: null,
		dueDate: null,
	};
};

export const createEmptyInvoice = (): Invoice => {
	return {
		id: "",
		client: null,
		contactPerson: null,
		contractor: null,
		paymentDeadlineDays: 30,
		headerNote: null,
		footerNote: null,
		items: [],
		number: "",
		leftTotal: 0,
		grossTotal: 0,
		issueDate: null,
		dueDate: null,
		type: DocumentType.Invoice,
		status: DocumentStatus.Draft,
		project: null,
		netTotal: 0,
		notes: null,
		isProforma: false,
	};
};

export const createEmptyCreditNote = (): CreditNote => {
	// Fixed values: SelfBilling (Gutschriftverfahren) + Purchase (we pay the supplier)
	const kind = CreditNoteKind.SelfBilling;
	const documentRole = DocumentRole.Purchase;
	return {
		id: "",
		client: null,
		contactPerson: null,
		contractor: null,
		paymentDeadlineDays: 30,
		headerNote: null,
		footerNote: null,
		items: [],
		number: "",
		leftTotal: 0,
		grossTotal: 0,
		issueDate: null,
		dueDate: null,
		type: DocumentType.CreditNote,
		status: DocumentStatus.Draft,
		project: null,
		netTotal: 0,
		notes: null,
		kind,
		documentRole,
		settlesInvoiceId: null,
		isProforma: false,
	};
};

export const updateField = <TModel, K extends keyof TModel>(
	setInvoice: (value: SetStateAction<TModel>) => void,
	key: K,
	value: TModel[K],
) => {
	setInvoice((prev: TModel) => ({ ...prev, [key]: value }));
};

export const updateInvoiceField = <K extends keyof Invoice>(
	setInvoice: (value: SetStateAction<Invoice>) => void,
	key: K,
	value: Invoice[K],
) => {
	setInvoice((prev: Invoice) => ({ ...prev, [key]: value }));
};

// Union type for components that can handle Invoice, CreditNote, and Voucher
export type CommercialDocument = Invoice | CreditNote;

// Type guard to check if a document is a CreditNote
export const isCreditNote = (doc: CommercialDocument): doc is CreditNote => {
	return doc.type === DocumentType.CreditNote;
};

// Type guard to check if a document is an Invoice
export const isInvoice = (doc: CommercialDocument): doc is Invoice => {
	return doc.type === DocumentType.Invoice && "client" in doc;
};
